/**
 * Lade- und Synchronisationszustände — **benannt**, nicht angedeutet.
 *
 * `07` §7.14 verlangt wörtlich, sie „verständlich zu benennen". Der Grund
 * steht in `07` §7.9: Die Monteur-App arbeitet offline, und ein Benutzer muss
 * jederzeit wissen, ob seine Eingabe schon beim Büro ist oder noch auf dem
 * Gerät liegt. Ein drehender Kreis beantwortet das nicht — er sagt „etwas
 * passiert", nicht „was passiert und was gilt gerade".
 *
 * Deshalb hat jeder Zustand hier einen **Namen** und einen erklärenden Satz,
 * und beides steht als Text da. Ein Wartezustand ohne Text ist die häufigste
 * Stelle, an der eine Oberfläche den Benutzer allein lässt.
 *
 * `wartet` ist der Zustand, den man vergisst: Er beschreibt nicht „lädt
 * gerade", sondern „liegt hier und wartet auf Netz". Ohne ihn hätte ein
 * Monteur im Funkloch dasselbe Bild wie einer mit langsamer Verbindung.
 */

import type { ReactNode } from "react";

export const LADEARTEN = ["laedt", "wartet", "fertig", "fehler"] as const;
export type Ladeart = (typeof LADEARTEN)[number];

const VORGABETEXT: Record<Ladeart, string> = {
  laedt: "Wird geladen",
  wartet: "Wartet auf Verbindung",
  fertig: "Gespeichert",
  fehler: "Nicht gespeichert",
};

export interface LadezustandEigenschaften {
  readonly art: Ladeart;
  /** Überschreibt den Vorgabetext, wenn der Zusammenhang genauer ist. */
  readonly text?: string;
  /** Was der Benutzer jetzt tun kann — bei `fehler` und `wartet` wichtig. */
  readonly hinweis?: ReactNode;
}

export function Ladezustand({ art, text, hinweis }: LadezustandEigenschaften) {
  return (
    <p
      className={`ladezustand ladezustand--${art}`}
      // `polite`, nicht `assertive`: Ein Statuswechsel unterbricht nicht, was
      // der Benutzer gerade tut. `assertive` wäre für „Ihre Eingabe ist
      // verloren" richtig, nicht für „wird gespeichert".
      role="status"
      aria-live="polite"
    >
      <span className="ladezustand__text">{text ?? VORGABETEXT[art]}</span>
      {hinweis ? <span className="ladezustand__hinweis">{hinweis}</span> : null}
    </p>
  );
}

/** Für Prüfrezepte und das Handbuch. */
export function vorgabetextFuer(art: Ladeart): string {
  return VORGABETEXT[art];
}
